import { useMemo } from 'react';

/** Accent hex — same recipe as ProductDrawer StepCardHeader (alpha on solid color). */
export interface StepConfig {
  id: number;
  title: string;
  description?: string;
  icon: React.ReactNode;
  /** Solid accent, e.g. #9333ea */
  color: string;
}

export interface UseStepHeaderProps {
  currentStep: number;
  totalSteps?: number;
  steps?: StepConfig[];
}

export const useStepHeader = ({ currentStep, totalSteps = 3, steps }: UseStepHeaderProps) => {
  const defaultSteps: StepConfig[] = useMemo(
    () => [
      {
        id: 1,
        title: 'Selección de Producto',
        description: 'Selecciona los productos que deseas agregar al inventario',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
            <line x1="7" y1="7" x2="7.01" y2="7"></line>
          </svg>
        ),
        color: '#9333ea',
      },
      {
        id: 2,
        title: 'Propiedades de piezas',
        description: 'Define las propiedades específicas de cada pieza',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="3" width="18" height="18" rx="2"></rect>
            <path d="M9 9h6v6H9z"></path>
            <path d="M9 1v6M15 1v6M9 15v8M15 15v8M1 9h6M1 15h6M17 9h6M17 15h6"></path>
          </svg>
        ),
        color: '#2563eb',
      },
      {
        id: 3,
        title: 'Confirmación',
        description: 'Revisa y confirma los datos antes de guardar',
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        ),
        color: '#10b981',
      },
    ],
    [],
  );

  const stepConfigs = useMemo(() => {
    return steps || defaultSteps.slice(0, totalSteps);
  }, [steps, defaultSteps, totalSteps]);

  const currentStepConfig = useMemo(() => {
    return stepConfigs.find((step) => step.id === currentStep) || stepConfigs[0];
  }, [stepConfigs, currentStep]);

  const isValidStep = useMemo(() => {
    return currentStep > 0 && currentStep <= stepConfigs.length;
  }, [currentStep, stepConfigs.length]);

  return {
    currentStepConfig,
    isValidStep,
    currentStep,
    totalSteps: stepConfigs.length,
  };
};
